import * as React from "react"
import { useState, useEffect, useRef, useMemo } from "react"
import {
  Box,
  Tab,
  Tabs,
  Button,
  Grid,
  Typography,
  Select,
  MenuItem,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material"
import { CircularProgress } from "@mui/material"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useQuery } from "react-query"
import { PLACEMENT_ROLES, DEFAULT_ANGLE } from "./constants"
import useStickyState from "../hooks/useStickyState"
import {
  Link as RouterLink,
} from "react-router-dom"
import { floodFill } from "../utils/imageFunctions"
import SearchIcon from "@mui/icons-material/Search"
import { Alert } from "@mui/material"
import { displayAtGym } from "../utils/gymApi"

export default function SearchClimbs() {
  const [activeTab, setActiveTab] = useStickyState(0, "search-tab")

  return (
    <Box sx={{ maxWidth: 600, margin: "0 auto", px: 1 }}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        centered
        sx={{ mb: 1 }}
      >
        <Tab label="Search" />
        <Tab label="Search by Holds" />
      </Tabs>
      <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
        <TextSearch />
      </Box>
      <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
        <HoldSearch />
      </Box>
    </Box>
  )
}

// Column indices for the compact climb arrays from the backend
const UUID = 0
const NAME = 1
const SETTER = 2
const DIFFICULTY = 3
const ASCENTS = 4
const QUALITY = 5
const CREATED = 6
const SEARCH_TEXT = 7 // added client-side during preprocessing

function TextSearch() {
  const [searchText, setSearchText] = useState("")
  const [minGrade, setMinGrade] = useStickyState(0, "search-min-grade")
  const [maxGrade, setMaxGrade] = useStickyState(99, "search-max-grade")
  const [sortBy, setSortBy] = useStickyState("popular", "search-sort")
  const [angle] = useStickyState(DEFAULT_ANGLE, "angle")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const gymSlug = searchParams.get("gym")
  const gymSlot = searchParams.get("slot")
  const pickingForGym = gymSlug && gymSlot

  const handleClimbClick = async (climbUuid) => {
    if (pickingForGym) {
      await displayAtGym(gymSlug, gymSlot, {
        type: "climb",
        climb_uuid: climbUuid,
        angle,
      })
      navigate(`/gym/${gymSlug}`)
    } else {
      navigate(`/climb/${climbUuid}`)
    }
  }

  const { data, isLoading } = useQuery(
    "searchIndex",
    async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/search_index`
      )
      return res.json()
    },
    {
      staleTime: 1000 * 60 * 60,
      cacheTime: 1000 * 60 * 60,
    }
  )

  // Preprocess: add lowercase search field for fast matching
  const processedClimbs = useMemo(() => {
    if (!data?.climbs) return []
    return data.climbs.map((c) => [
      ...c,
      (c[NAME] + " " + c[SETTER]).toLowerCase(),
    ])
  }, [data])

  // Grade lookup map: { difficultyNumber: "gradeName" }
  const gradeMap = useMemo(() => {
    if (!data?.grades) return {}
    const map = {}
    data.grades.forEach(([k, v]) => {
      map[k] = v
    })
    return map
  }, [data])

  // Filter and sort climbs client-side
  const filteredClimbs = useMemo(() => {
    const query = searchText.toLowerCase().trim()
    let results = processedClimbs

    // Text filter
    if (query) {
      // Support multi-word search: all words must match
      const words = query.split(/\s+/)
      results = results.filter((c) =>
        words.every((w) => c[SEARCH_TEXT].includes(w))
      )
    }

    // Grade filter
    if (minGrade > 0) {
      results = results.filter((c) => c[DIFFICULTY] >= minGrade)
    }
    if (maxGrade < 99) {
      results = results.filter(
        (c) => c[DIFFICULTY] <= maxGrade || c[DIFFICULTY] === 0
      )
    }

    // Sort
    const sorted = [...results]
    switch (sortBy) {
      case "popular":
        sorted.sort((a, b) => b[ASCENTS] - a[ASCENTS])
        break
      case "quality":
        sorted.sort((a, b) => b[QUALITY] - a[QUALITY])
        break
      case "newest":
        sorted.sort((a, b) => (b[CREATED] || "").localeCompare(a[CREATED] || ""))
        break
      case "oldest":
        sorted.sort((a, b) => (a[CREATED] || "").localeCompare(b[CREATED] || ""))
        break
      default:
        break
    }

    return sorted.slice(0, 100)
  }, [searchText, minGrade, maxGrade, sortBy, processedClimbs])

  const resultCount = useMemo(() => {
    // Count without the slice to show accurate "100+" indicator
    const query = searchText.toLowerCase().trim()
    let count = processedClimbs.length
    if (query) {
      const words = query.split(/\s+/)
      count = processedClimbs.filter((c) =>
        words.every((w) => c[SEARCH_TEXT].includes(w))
      ).length
    }
    if (minGrade > 0) {
      // approximate — the filtered list already accounts for this
    }
    return filteredClimbs.length >= 100 ? "100+" : filteredClimbs.length
  }, [filteredClimbs, searchText, processedClimbs, minGrade])

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {pickingForGym && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Picking problem {gymSlot} for <b>{gymSlug}</b> — click a climb to
          display it there.
        </Alert>
      )}
      <TextField
        fullWidth
        placeholder="Search by name or setter..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        size="small"
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 110, flex: 1 }}>
          <InputLabel>Min Grade</InputLabel>
          <Select
            value={minGrade}
            label="Min Grade"
            onChange={(e) => setMinGrade(e.target.value)}
          >
            <MenuItem value={0}>Any</MenuItem>
            {data?.grades?.map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110, flex: 1 }}>
          <InputLabel>Max Grade</InputLabel>
          <Select
            value={maxGrade}
            label="Max Grade"
            onChange={(e) => setMaxGrade(e.target.value)}
          >
            <MenuItem value={99}>Any</MenuItem>
            {data?.grades?.map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="popular">Most Popular</MenuItem>
            <MenuItem value="quality">Best Quality</MenuItem>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
        {resultCount} results
      </Typography>
      <List sx={{ maxHeight: "65vh", overflow: "auto" }} dense>
        {filteredClimbs.map((climb) => (
          <ListItem
            button
            key={climb[UUID]}
            onClick={() => handleClimbClick(climb[UUID])}
            divider
          >
            <ListItemText
              primary={climb[NAME]}
              secondary={`${gradeMap[climb[DIFFICULTY]] || "Ungraded"} · ${climb[SETTER]} · ${climb[ASCENTS]} ascent${climb[ASCENTS] !== 1 ? "s" : ""}`}
            />
          </ListItem>
        ))}
        {filteredClimbs.length === 0 && searchText && (
          <ListItem>
            <ListItemText
              primary="No results found"
              secondary="Try a different search term or adjust filters"
            />
          </ListItem>
        )}
      </List>
    </Box>
  )
}

function HoldSearch() {
  const [scoredClimbs, setScoredClimbs] = useState([])
  const canvasRef = useRef(null)
  const inMemoryCanvasRef = useRef(null)
  const imageDataRef = useRef(null)
  const backgroundImageCanvasRef = useRef(null)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [angle, setAngle] = useStickyState(DEFAULT_ANGLE, "angle")
  const [selectedHoldCenters, setSelectedHoldCenters] = useState([])
  const [climb, setClimb] = useState(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    inMemoryCanvasRef.current = document.createElement("canvas")
  }, [])

  useEffect(() => {
    setIsImageLoading(true)
    const canvas = canvasRef.current
    const context = canvas.getContext("2d")
    const inMemoryCanvas = inMemoryCanvasRef.current
    const inMemoryContext = inMemoryCanvas.getContext("2d")
    const image = new Image()
    image.src = "/images/kilter-holds.png"
    const backgroundImage = new Image()
    backgroundImage.src = "/images/kilter.jpg"
    image.onload = () => {
      const parent = canvas.parentElement
      canvas.width = parent.offsetWidth
      canvas.height = parent.offsetHeight
      inMemoryCanvas.width = image.width
      inMemoryCanvas.height = image.height
      inMemoryContext.drawImage(image, 0, 0, image.width, image.height)

      imageDataRef.current = inMemoryContext.getImageData(
        0,
        0,
        inMemoryCanvas.width,
        inMemoryCanvas.height
      )
      const imageData = imageDataRef.current
      const data = imageData.data
      const rows = imageData.height
      const cols = imageData.width
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          let i = (row * cols + col) * 4
          data[i + 3] = 64
        }
      }
      inMemoryContext.putImageData(imageData, 0, 0)
      context.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
      setIsImageLoading(false)
    }

    backgroundImage.onload = () => {
      backgroundImageCanvasRef.current = document.createElement("canvas")
      const backgroundImageCanvas = backgroundImageCanvasRef.current
      const backgroundImageContext = backgroundImageCanvas.getContext("2d")
      backgroundImageCanvas.width = backgroundImage.width
      backgroundImageCanvas.height = backgroundImage.height
      backgroundImageContext.drawImage(
        backgroundImage,
        0,
        0,
        backgroundImage.width,
        backgroundImage.height
      )
    }

    const handleClick = (event) => {
      let inMemoryCanvas = inMemoryCanvasRef.current
      let inMemoryContext = inMemoryCanvas.getContext("2d")
      let canvas = canvasRef.current
      let ctx = canvas.getContext("2d")

      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const xInMemory = Math.floor((x * inMemoryCanvas.width) / canvas.width)
      const yInMemory = Math.floor((y * inMemoryCanvas.height) / canvas.height)

      let imageData = inMemoryContext.getImageData(
        0,
        0,
        inMemoryCanvas.width,
        inMemoryCanvas.height
      )
      let fillCenter = floodFill(imageData, xInMemory, yInMemory, [
        255, 0, 0, 255,
      ])
      if (fillCenter) {
        setSelectedHoldCenters((prev) => {
          if (prev.indexOf(fillCenter) > -1) {
            return prev.filter((center) => center !== fillCenter)
          } else {
            return [...prev, fillCenter]
          }
        })
      }
      inMemoryContext.putImageData(imageData, 0, 0)
      ctx.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
    }

    canvas.addEventListener("click", handleClick)
    return () => {
      canvas.removeEventListener("click", handleClick)
    }
  }, [])

  const updateCanvasSize = () => {
    const inMemoryCanvas = inMemoryCanvasRef.current
    const canvas = canvasRef.current
    const parent = canvas.parentElement
    const ctx = canvas.getContext("2d")
    canvas.width = parent.offsetWidth
    canvas.height = parent.offsetHeight
    ctx.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
  }

  const drawClimb = () => {
    if (!climb) return
    const inMemoryCanvas = inMemoryCanvasRef.current
    const inMemoryContext = inMemoryCanvas.getContext("2d")
    const imageData = inMemoryContext.getImageData(
      0,
      0,
      inMemoryCanvas.width,
      inMemoryCanvas.height
    )
    const { placements, placement_roles } = climb
    let gridSpacingX = 2 * (106.0 / 4000) * 1020
    let gridSpacingY = 2 * (82.9 / 3000) * 1024
    let height = 1024
    let scaleX = gridSpacingX / 8.0
    let scaleY = gridSpacingY / 8
    let offsetX = 25
    let offsetY = -90

    placements.forEach((placement, index) => {
      const { x, y } = placement
      const yInMemory = Math.floor(height - y * scaleY - offsetY)
      const xInMemory = Math.floor(x * scaleX + offsetX)
      const color = PLACEMENT_ROLES[placement_roles[index]]?.color || [
        255, 0, 255, 255,
      ]
      floodFill(imageData, xInMemory, yInMemory, color)
    })
    inMemoryContext.putImageData(imageData, 0, 0)
    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    ctx.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
  }

  const drawSelectedHoldCenters = () => {
    if (!selectedHoldCenters.length) return
    const inMemoryCanvas = inMemoryCanvasRef.current
    const inMemoryContext = inMemoryCanvas.getContext("2d")

    selectedHoldCenters.forEach((center) => {
      const [x, y] = center
      inMemoryContext.beginPath()
      inMemoryContext.arc(x, y, 25, 0, 2 * Math.PI)
      inMemoryContext.strokeStyle = "rgba(255, 255, 0, 1)"
      inMemoryContext.lineWidth = 2
      inMemoryContext.stroke()
    })
    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    ctx.drawImage(inMemoryCanvas, 0, 0, canvas.width, canvas.height)
  }

  const clearImage = () => {
    const inMemoryCanvas = inMemoryCanvasRef.current
    const inMemoryContext = inMemoryCanvas.getContext("2d")
    const imageData = imageDataRef.current
    if (!imageData) return
    inMemoryContext.putImageData(imageData, 0, 0)
    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  useEffect(() => {
    clearImage()
    drawClimb()
    drawSelectedHoldCenters()
  }, [climb, isImageLoading, selectedHoldCenters])

  useEffect(() => {
    updateCanvasSize()
    window.addEventListener("resize", updateCanvasSize)
    return () => {
      window.removeEventListener("resize", updateCanvasSize)
    }
  }, [])

  const handleAngleChange = (event) => {
    setAngle(event.target.value)
  }

  const search = async () => {
    if (!selectedHoldCenters.length) return
    setLoading(true)
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/search_climbs`,
        {
          method: "POST",
          body: JSON.stringify({ hold_centers: selectedHoldCenters }),
          headers: { "Content-Type": "application/json" },
        }
      )
      const climbs = await response.json()
      if (climbs?.length) {
        setClimb(climbs[0][1])
        setScoredClimbs(climbs)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onChangeClimb = (climb) => () => {
    setClimb(climb)
  }

  return (
    <Grid container sx={{ backgroundColor: "transparent" }}>
      <Grid item xs={12}>
        {climb && (
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="h6">
              {climb.name} by {climb.setter_username}
            </Typography>
            {climb.stats && (
              <Select
                value={angle}
                onChange={handleAngleChange}
                size="small"
              >
                {climb.stats.map((stat) => (
                  <MenuItem key={stat.angle} value={stat.angle}>
                    {stat.display_difficulty} @ {stat.angle}
                  </MenuItem>
                ))}
              </Select>
            )}
            {climb.uuid && (
              <Box sx={{ mt: 0.5 }}>
                <Link
                  component={RouterLink}
                  to={`/climb/${climb.uuid}`}
                >
                  View full details
                </Link>
              </Box>
            )}
          </Box>
        )}
      </Grid>
      <Grid item xs={12} sx={{ backgroundColor: "transparent" }}>
        <Box
          id="canvas-container"
          sx={{
            position: "relative",
            width: { xs: "100vw", md: "80%" },
            maxWidth: "70vh",
            margin: "auto auto",
            "&::before": {
              content: '""',
              display: "block",
              paddingTop: "100%",
            },
            "& > *": {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              maxWidth: "100%",
              maxHeight: "100%",
              width: "100%",
              aspectRatio: "1/1",
              opacity: 1.0,
            }}
          >
            <img
              src="/images/kilter.jpg"
              alt="climb"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "transparent",
              }}
            />
          </Box>
          <Box
            sx={{
              position: "absolute",
              maxWidth: "100%",
              maxHeight: "100%",
              width: "100%",
              aspectRatio: "1/1",
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "transparent",
              }}
            />
          </Box>
        </Box>
      </Grid>
      <Grid container item xs={12} sx={{ backgroundColor: "transparent" }}>
        <Grid
          item
          sx={{
            backgroundColor: "transparent",
            width: { xs: "100vw", md: "80%" },
            maxWidth: "70vh",
            margin: "auto auto",
          }}
        >
          <Button variant="contained" fullWidth onClick={search}>
            Search
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        sx={{ backgroundColor: "transparent" }}
      >
        <Grid
          item
          sx={{
            backgroundColor: "transparent",
            width: { xs: "100vw", md: "80%" },
            maxWidth: "70vh",
            margin: "auto auto",
          }}
        >
          {loading && <CircularProgress />}
          <List sx={{ maxHeight: "50vh", overflow: "auto" }}>
            {scoredClimbs.map(([score, climb]) => (
              <ListItem button key={climb.uuid} onClick={onChangeClimb(climb)}>
                <ListItemText primary={climb.name} />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
    </Grid>
  )
}
